import { Button, Tooltip } from '@heroui/react'
import { MediaType, useEmptyTrash } from '@qse/api'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { TrashIcon } from '@qse/ui'
import { useIsLargeScreen } from '@qse/utils'
import { InfoIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { FileExplorer } from '@/components'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

interface SearchValues {
  search: string
  fileType?: string
  isDir?: boolean
  createdFrom?: string
  createdTo?: string
}

const Trash = () => {
  const query = useQuery()
  const searchQuery = query.get('query') || ''
  const fileType = query.get('fileType') || ''
  const isDir = query.get('isDir') || ''
  const createdFrom = query.get('createdFrom') || ''
  const createdTo = query.get('createdTo') || ''
  const [itemsNumber, setItemsNumber] = useState(0)
  const { mutateAsync: emptyTrash, isPending: isClearingTrash } = useEmptyTrash()
  const searchValues = useMemo<SearchValues>(
    () => ({
      search: searchQuery,
      fileType: fileType || undefined,
      isDir: isDir === 'true' ? true : undefined,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
    }),
    [searchQuery, fileType, isDir, createdFrom, createdTo],
  )

  // Search parameters for FileExplorer
  const searchParams = useMemo(
    () => ({
      limit: 50,
      location: 'TRASH' as const, // Use location to filter for trashed files
      search: searchValues.search || undefined,
      isDir: searchValues.isDir === true ? true : undefined,
      mediaType:
        searchValues.isDir === true ? undefined : (searchValues.fileType as MediaType) || undefined,
      createdFrom: searchValues.createdFrom || undefined,
      createdTo: searchValues.createdTo || undefined,
    }),
    [searchValues],
  )

  // Dynamic empty state config based on search/filter state
  const emptyStateConfig = useMemo(() => {
    // Check if there are any active search/filter parameters
    const hasActiveSearch =
      searchValues.search ||
      searchValues.fileType ||
      searchValues.isDir !== undefined ||
      searchValues.createdFrom ||
      searchValues.createdTo

    return {
      icon: hasActiveSearch ? (
        <svg
          className="w-12 h-12 text-gray-400 dark:text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      ) : (
        <svg
          className="w-12 h-12 text-gray-400 dark:text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      ),
      title: hasActiveSearch
        ? searchValues.search
          ? `No files found for "${searchValues.search}"`
          : 'No results found'
        : 'Trash is empty',
      description: hasActiveSearch
        ? searchValues.search
          ? `We couldn't find any files in trash matching "${searchValues.search}". Try adjusting your search terms or filters.`
          : 'No files match the current filters. Try adjusting your search criteria.'
        : 'Deleted files and folders will appear here. You can restore them or delete them permanently.',
    }
  }, [searchValues])

  // Generate title based on active filters
  const getTitle = () => {
    const parts = []

    if (searchValues.search) {
      parts.push(`Search: "${searchValues.search}"`)
    }

    if (searchValues.fileType) {
      const fileTypeLabels: Record<string, string> = {
        FOLDER: 'Folders',
        DOCUMENT: 'Documents',
        IMAGE: 'Images',
        VIDEO: 'Videos',
        AUDIO: 'Audio',
        OTHER: 'Other Files',
      }

      parts.push(`Type: ${fileTypeLabels[searchValues.fileType] || searchValues.fileType}`)
    }

    if (searchValues.createdFrom || searchValues.createdTo) {
      parts.push('Date Filtered')
    }

    return parts.length > 0 ? parts.join(' | ') : 'Trash'
  }

  const handleClearTrash = () => {
    const config = {
      modalHeader: {
        title: 'Confirmation',
        showCloseButton: true,
      },

      title: 'Empty Trash',
      message: "All items in trash will be deleted forever. This can't be undone.",
      icon: '/assets/svg/delete.svg',
      actions: [
        {
          label: 'Cancel',
          color: 'default',
          variant: 'flat',
        },
        {
          label: 'Delete Forever',
          color: 'danger',
          onPress: () => {
            emptyTrash()
          },
        },
      ],
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[660px]',
      modelActive: ModelControlEnum.CONFIRMATION,
      toggleModel: true,
    })
  }
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const isLargeScreen = useIsLargeScreen()

  return (
    <div className="flex flex-col gap-4 text-gray-900 dark:text-white min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 px-4 md:px-[30px]">
        <div className="flex items-center gap-2">
          <h1 className="lg:text-2xl text-xl font-bold text-gray-900 dark:text-white">
            {getTitle()}
          </h1>
          {!isLargeScreen && (
            <Tooltip
              className="block lg:hidden"
              content="Items in trash will be deleted forever after 30 days"
              isOpen={isTooltipOpen}
              onOpenChange={setIsTooltipOpen}
            >
              <InfoIcon className="w-4 h-4" onClick={() => setIsTooltipOpen(!isTooltipOpen)} />
            </Tooltip>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="bg-yellow-100 dark:bg-yellow-900/80 text-yellow-800 dark:text-yellow-400 text-sm lg:text-lg px-4 py-2 rounded-xl font-sm dark:shadow-lg hidden lg:block">
            Items in trash will be deleted forever after 30 days
          </div>
          {itemsNumber > 0 && (
            <Button
              color="danger"
              isDisabled={isClearingTrash}
              isLoading={isClearingTrash}
              radius="sm"
              size={isLargeScreen ? 'md' : 'sm'}
              startContent={<TrashIcon className="w-4 h-4" />}
              type="button"
              onPress={handleClearTrash}
            >
              {isClearingTrash ? 'Clearing...' : 'Clear Trash'}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 pb-32">
        <FileExplorer
          emptyStateConfig={emptyStateConfig}
          searchParams={searchParams}
          setItemsNumber={setItemsNumber}
          showRestore={true}
          title=""
          trashMode={true}
          uploadEnabled={false}
        />
      </div>
    </div>
  )
}

export default Trash
